//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { PojoSet } from 'pojo-sets';

import { ICuralateSession } from '@app/basic-session/ICuralateSession';
import { Permissions } from '@models/Permissions';
import { SessionMode } from '@moderation-mode/constants/SessionMode';
import { getHasCapabilityOverride } from '@moderation-mode/moderationModeDevOverride';
import { isTruthy } from '@utils/filterPredicates';

interface ModeDerivationInput {
  hasMergeEnabled: boolean;
  isCuralateEmployee: boolean;
  isModeratorCapable: boolean;
  storedMode: SessionMode | undefined;
}

// ShareV2 vendors and legacy syndication clients run the separate Connect
// instance, so the merge stays off for them (also avoids a guard redirect loop
// via syndicationDefaultState).
export const getIsModerationMergeEnabled = (
  session: ICuralateSession,
): boolean =>
  !session.client.isShareV2Vendor &&
  !session.client.isSyndication &&
  isTruthy(session.features[FeatureNames.MediaLibrarySourcedOrganicContent]) &&
  isTruthy(session.features[FeatureNames.BvModeration]) &&
  // TODO: PD-330267 - Remove feature check once ModeratorVibeView is released
  isTruthy(session.features[FeatureNames.ModeratorVibeView]);

export const getIsModeratorCapable = (permissions: PojoSet<string>): boolean =>
  isTruthy(permissions[Permissions.ModerationView]) ||
  getHasCapabilityOverride();

/**
 * Employees switch freely (stored preference, defaults Client); everyone else
 * has a fixed view from moderation:view, so their stored mode is ignored.
 */
export const deriveSessionMode = ({
  hasMergeEnabled,
  isCuralateEmployee,
  isModeratorCapable,
  storedMode,
}: ModeDerivationInput): SessionMode => {
  if (!hasMergeEnabled) {
    return SessionMode.Client;
  }

  if (isCuralateEmployee) {
    return storedMode ?? SessionMode.Client;
  }

  return isModeratorCapable ? SessionMode.Moderator : SessionMode.Client;
};
